import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    TextField,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Paper,
} from '@mui/material';
import { Check, Search } from '@mui/icons-material';
import { useGetLocationManagersQuery } from '../../../../../api/preHQ';

const AdditionalDetails = ({ form }) => {
    const {
        watch,
        setValue,
        formState: { errors },
    } = form;
    const formValues = watch();

    // Use the RTK Query hook directly
    const { data: hiringManagers, isLoading } = useGetLocationManagersQuery();

    // State for hiring manager dropdown
    const [managersList, setManagersList] = useState([]);
    const [managerIsOpen, setManagerIsOpen] = useState(false);
    const [managerSearch, setManagerSearch] = useState('');
    const managerDropdownRef = useRef(null);

    // Get current hiringManager value
    const hiringManagerValue = formValues.hiringManager;

    // Employment types
    const employmentTypes = [
        { value: 'full-time', label: 'Full Time' },
        { value: 'part-time', label: 'Part Time' },
        { value: 'contract', label: 'Contract' },
    ];

    // Transform hiring managers data and sync search state with form values
    useEffect(() => {
        if (hiringManagers && hiringManagers.employeeList) {
            const formattedManagers = hiringManagers.employeeList.map((employee) => ({
                value: employee.id,
                label: employee.fullName,
            }));

            setManagersList(formattedManagers);
        }

        // Sync search state with form values
        if (
            hiringManagerValue &&
            typeof hiringManagerValue === 'object' &&
            'label' in hiringManagerValue &&
            hiringManagerValue.label
        ) {
            setManagerSearch(hiringManagerValue.label);
        }
    }, [hiringManagers, hiringManagerValue]);

    // Handle clicks outside dropdown
    useEffect(() => {
        function handleClickOutside(event) {
            if (managerDropdownRef.current && !managerDropdownRef.current.contains(event.target)) {
                setManagerIsOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleManagerSelect = (manager) => {
        setValue(
            'hiringManager',
            {
                id: String(manager.value),
                label: String(manager.label),
            },
            { shouldValidate: true },
        );
        setManagerIsOpen(false);
        setManagerSearch(manager.label);
    };

    return (
        <Box>
            <Typography variant="h6" component="h2" sx={{ mb: 2, fontWeight: 600 }}>
                Additional Details
            </Typography>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small" error={!!errors.employmentType}>
                        <InputLabel>Employment Type</InputLabel>
                        <Select
                            label="Employment Type"
                            value={formValues.employmentType || ''}
                            onChange={(e) => setValue('employmentType', e.target.value, { shouldValidate: true })}
                        >
                            {employmentTypes.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                    {type.label}
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.employmentType && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                {errors.employmentType.message}
                            </Typography>
                        )}
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Box ref={managerDropdownRef} sx={{ position: 'relative' }}>
                        <TextField
                            label="Hiring Manager"
                            placeholder="Search hiring managers..."
                            value={managerSearch}
                            onChange={(e) => {
                                const value = e.target.value;
                                setManagerSearch(value);
                            }}
                            onFocus={() => {
                                setManagerIsOpen(true);
                                if (hiringManagerValue?.label) {
                                    setManagerSearch('');
                                }
                            }}
                            onBlur={() => {
                                if (!managerSearch && hiringManagerValue?.label) {
                                    setManagerSearch(hiringManagerValue.label);
                                }
                                setTimeout(() => {
                                    setManagerIsOpen(false);
                                }, 100);
                            }}
                            error={!!errors.hiringManager}
                            helperText={errors.hiringManager?.message}
                            fullWidth
                            size="small"
                            InputProps={{
                                endAdornment: isLoading ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    <Search sx={{ color: 'grey.400' }} />
                                ),
                            }}
                        />

                        {managerIsOpen && (
                            <Paper
                                sx={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    right: 0,
                                    zIndex: 1000,
                                    mt: 1,
                                    maxHeight: 240,
                                    overflow: 'auto',
                                    boxShadow: 3,
                                }}
                            >
                                <List dense>
                                    {managersList.length === 0 && managerSearch ? (
                                        <ListItem
                                            button
                                            onClick={() =>
                                                handleManagerSelect({
                                                    value: managerSearch,
                                                    label: managerSearch,
                                                })
                                            }
                                        >
                                            <ListItemText primary={managerSearch} />
                                        </ListItem>
                                    ) : managersList.length === 0 ? (
                                        <ListItem>
                                            <ListItemText
                                                primary="No hiring managers found"
                                                sx={{ color: 'text.secondary' }}
                                            />
                                        </ListItem>
                                    ) : (
                                        (() => {
                                            let displayManagers = [...managersList];
                                            if (hiringManagerValue?.id) {
                                                const selectedManager = {
                                                    value: hiringManagerValue.id,
                                                    label: hiringManagerValue.label,
                                                };
                                                // Remove the selected item if it's already in the list to avoid duplicates
                                                displayManagers = displayManagers.filter(
                                                    (manager) => manager.value !== selectedManager.value,
                                                );
                                                // Add the selected item to the top of the list
                                                displayManagers.unshift(selectedManager);
                                            }

                                            return displayManagers
                                                .filter((manager) =>
                                                    manager.label.toLowerCase().includes(managerSearch.toLowerCase()),
                                                )
                                                .map((manager) => (
                                                    <ListItem
                                                        key={manager.value}
                                                        button
                                                        onMouseDown={() => handleManagerSelect(manager)}
                                                        sx={{
                                                            bgcolor:
                                                                hiringManagerValue?.id === manager.value
                                                                    ? 'action.selected'
                                                                    : 'transparent',
                                                        }}
                                                    >
                                                        <ListItemText primary={manager.label} />
                                                        <ListItemIcon sx={{ minWidth: 0, ml: 1 }}>
                                                            <Check
                                                                sx={{
                                                                    opacity:
                                                                        hiringManagerValue?.id === manager.value
                                                                            ? 1
                                                                            : 0,
                                                                    color: 'primary.main',
                                                                }}
                                                            />
                                                        </ListItemIcon>
                                                    </ListItem>
                                                ));
                                        })()
                                    )}
                                </List>
                            </Paper>
                        )}
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default AdditionalDetails;
